import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { BBox, HighlightStroke } from "@tebiki/shared";
import { AppBar, Tag, Banner, Btn } from "../components/index.js";
import { Icon } from "../components/Icon.js";
import { Highlighter, HI_PALETTE, HI_DEFAULT, type HighlightTool } from "../components/Highlighter.js";
import { MarkdownText } from "../components/MarkdownText.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { useSurface } from "../lib/useSurface.js";
import { useAsync } from "../lib/useAsync.js";
import { useSelectionWithin } from "../lib/useSelectionWithin.js";
import { manuals, mediaUrl } from "../lib/api.js";
import { frameToBbox } from "../lib/bbox.js";
import { mmss, type StepWithCandidates } from "../lib/demo.js";

/** Annotate has two annotation modes: the operation-target frame (bbox) and freehand marker. */
type AnnMode = "bbox" | "marker";

/** A normalised rect in image-relative percentages (0..100), the editor's working unit. */
interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Shared [y0,x0,y1,x1] (0..1000) -> editor Rect (% of image). */
function bboxToRect(bbox: BBox | null | undefined): Rect | null {
  if (!bbox) return null;
  const [y0, x0, y1, x1] = bbox;
  return {
    top: (y0 / 1000) * 100,
    left: (x0 / 1000) * 100,
    width: ((x1 - x0) / 1000) * 100,
    height: ((y1 - y0) / 1000) * 100,
  };
}

/** Default centred rect for steps with no detected bbox yet (so the user can drag it onto target). */
const DEFAULT_RECT: Rect = { top: 35, left: 35, width: 30, height: 30 };

const clampPct = (n: number) => Math.max(0, Math.min(100, n));

/** Resize handles: corners + edge midpoints. id encodes which sides move. */
type HandleId = "nw" | "ne" | "sw" | "se" | "n" | "s" | "w" | "e";
const HANDLES: { id: HandleId; cx: number; cy: number; cursor: string }[] = [
  { id: "nw", cx: 0, cy: 0, cursor: "nwse-resize" },
  { id: "n", cx: 0.5, cy: 0, cursor: "ns-resize" },
  { id: "ne", cx: 1, cy: 0, cursor: "nesw-resize" },
  { id: "e", cx: 1, cy: 0.5, cursor: "ew-resize" },
  { id: "se", cx: 1, cy: 1, cursor: "nwse-resize" },
  { id: "s", cx: 0.5, cy: 1, cursor: "ns-resize" },
  { id: "sw", cx: 0, cy: 1, cursor: "nesw-resize" },
  { id: "w", cx: 0, cy: 0.5, cursor: "ew-resize" },
];

const MIN_PCT = 4; // never let the box collapse below 4% in either axis

interface DragState {
  mode: "move" | HandleId;
  startX: number;
  startY: number;
  start: Rect;
  /** image element bounds at gesture start (px) for pointer->% conversion */
  rectW: number;
  rectH: number;
}

/**
 * Annotate — operation-target (bbox) editor. Background = the step's chosen frame; a lime
 * bbox is dragged/resized with Pointer Events (iPad-friendly) in image-relative %, persisted
 * as the shared [y0,x0,y1,x1] 0..1000 convention. AI re-detect + label edit + save + step nav.
 * Mirrors ui/screens/annotate.html (toolbar / trim marks / lime frame).
 */
export function Annotate() {
  useSurface("field");
  const { id = "" } = useParams();
  const [params] = useSearchParams();

  const { data, loading, error, offline, reload } = useAsync(() => manuals.get(id), [id]);

  const steps = (data?.steps ?? []) as StepWithCandidates[];

  // Active step index — honour ?step=<stepId> deep link from Preview, else first.
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const sid = params.get("step");
    if (sid) {
      const i = steps.findIndex((s) => s.stepId === sid);
      if (i >= 0) setIdx(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length]);

  const step = steps[idx];
  const imgSrc = mediaUrl(step?.imageUrl);

  // Editable working copy of the active step's annotation.
  const [rect, setRect] = useState<Rect | null>(null);
  const [label, setLabel] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Annotation mode + marker working copy (strokes + key-phrase terms).
  const [mode, setMode] = useState<AnnMode>("bbox");
  const [strokes, setStrokes] = useState<HighlightStroke[]>([]);
  const [terms, setTerms] = useState<string[]>([]);
  const [hiTool, setHiTool] = useState<HighlightTool>("draw");
  const [hiColor, setHiColor] = useState<string>(HI_DEFAULT);
  const [marking, setMarking] = useState(false);
  const [selText, setSelText] = useState("");
  // Body container for cross-OS text-selection capture (selectionchange works where mouseup doesn't).
  const bodyRef = useRef<HTMLDivElement>(null);
  useSelectionWithin(bodyRef, true, setSelText);

  // Reset the working copy whenever the active step changes.
  useEffect(() => {
    if (!step) return;
    setRect(bboxToRect(step.bbox));
    setLabel(step.objectLabel ?? "");
    setStrokes(step.highlights?.strokes ?? []);
    setTerms(step.highlights?.terms ?? []);
    setHiTool("draw");
    setDirty(false);
    setNotice(null);
    setSelText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.stepId]);

  // Mark strokes dirty when edited via the Highlighter.
  const onStrokesChange = useCallback((next: HighlightStroke[]) => {
    setStrokes(next);
    setDirty(true);
  }, []);

  function undoStroke() {
    setStrokes((s) => s.slice(0, -1));
    setDirty(true);
  }
  function clearStrokes() {
    setStrokes([]);
    setDirty(true);
  }
  function addTerm(raw: string) {
    const t = raw.trim();
    if (!t) return;
    setTerms((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setDirty(true);
  }
  function removeTerm(t: string) {
    setTerms((prev) => prev.filter((x) => x !== t));
    setDirty(true);
  }
  /** Read the user's current text selection (body text) and add it as a key phrase. */
  function markSelection() {
    const sel = typeof window !== "undefined" ? window.getSelection()?.toString() ?? "" : "";
    const text = (sel || selText).trim();
    if (!text) {
      setNotice("マークするテキストを本文から選択してください。");
      return;
    }
    addTerm(text);
    setSelText("");
    if (typeof window !== "undefined") window.getSelection()?.removeAllRanges();
  }

  async function runAutoMark() {
    if (!step) return;
    setMarking(true);
    setNotice(null);
    try {
      const res = await manuals.autoHighlight(id, step.stepId);
      setStrokes(res.highlights.strokes ?? []);
      setTerms(res.highlights.terms ?? []);
      if (step) step.highlights = res.highlights;
      setDirty(false);
      setNotice(
        res.highlights.terms.length || res.highlights.strokes.length
          ? "AIが重要ポイントをマークしました。"
          : "マークできる重要ポイントが見つかりませんでした。",
      );
    } catch {
      setNotice("AI自動マークに失敗しました。時間をおいて再試行してください。");
    } finally {
      setMarking(false);
    }
  }

  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const applyDrag = useCallback((clientX: number, clientY: number) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = ((clientX - d.startX) / d.rectW) * 100;
    const dy = ((clientY - d.startY) / d.rectH) * 100;
    const s = d.start;
    let next: Rect;
    if (d.mode === "move") {
      next = {
        top: clampPct(Math.min(s.top + dy, 100 - s.height)),
        left: clampPct(Math.min(s.left + dx, 100 - s.width)),
        width: s.width,
        height: s.height,
      };
    } else {
      let { top, left, width, height } = s;
      const right = left + width;
      const bottom = top + height;
      if (d.mode.includes("n")) {
        const t = clampPct(Math.min(top + dy, bottom - MIN_PCT));
        height = bottom - t;
        top = t;
      }
      if (d.mode.includes("s")) {
        const b = clampPct(Math.max(bottom + dy, top + MIN_PCT));
        height = b - top;
      }
      if (d.mode.includes("w")) {
        const l = clampPct(Math.min(left + dx, right - MIN_PCT));
        width = right - l;
        left = l;
      }
      if (d.mode.includes("e")) {
        const r = clampPct(Math.max(right + dx, left + MIN_PCT));
        width = r - left;
      }
      next = { top, left, width, height };
    }
    setRect(next);
    setDirty(true);
  }, []);

  // Pointer move/up listeners live on window so a fast drag that leaves the box keeps tracking.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      applyDrag(e.clientX, e.clientY);
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [applyDrag]);

  function beginDrag(e: React.PointerEvent, mode: DragState["mode"]) {
    if (!rect || !stageRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const b = stageRef.current.getBoundingClientRect();
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      start: rect,
      rectW: b.width,
      rectH: b.height,
    };
  }

  /** Create a fresh default box (for steps detected as null / starting from scratch). */
  function addBox() {
    setRect(DEFAULT_RECT);
    setDirty(true);
  }

  async function runDetect() {
    if (!step) return;
    setDetecting(true);
    setNotice(null);
    try {
      const res = await manuals.detectStep(id, step.stepId);
      setRect(bboxToRect(res.bbox));
      setLabel(res.objectLabel ?? "");
      setDirty(true);
      setNotice(res.bbox ? "AIが操作対象を再検出しました。" : "操作対象を検出できませんでした。手動で囲んでください。");
    } catch {
      setNotice("自動検出に失敗しました。時間をおいて再試行してください。");
    } finally {
      setDetecting(false);
    }
  }

  async function save() {
    if (!step) return;
    setSaving(true);
    setNotice(null);
    try {
      // Save BOTH the frame (bbox/label) and the highlights regardless of the active mode.
      // The body key-phrase highlighter is now available in 枠 mode too, so a save must never
      // drop the other mode's work when the user switches between 枠 ↔ マーカー.
      const highlights = { strokes, terms };
      const bbox = rect ? frameToBbox(rect) : null;
      const objectLabel = label.trim() ? label.trim() : null;
      await manuals.updateStep(id, step.stepId, { bbox, objectLabel });
      await manuals.saveHighlights(id, step.stepId, highlights);
      // Reflect the saved values onto the loaded step so re-entry shows them.
      if (step) {
        step.highlights = highlights;
        step.bbox = bbox;
        step.objectLabel = objectLabel || undefined;
      }
      setDirty(false);
      setNotice("保存しました。");
    } catch {
      setNotice("保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  }

  const overlayLabel = useMemo(() => label.trim() || step?.objectLabel || "操作対象", [label, step?.objectLabel]);

  return (
    <div className="shell">
      <AppBar back={id ? `/preview/${id}` : "/preview/draft"} compactBrand>
        {mode === "marker" ? (
          <Tag dot color={strokes.length || terms.length ? "var(--hi)" : "var(--muted)"}>
            {strokes.length || terms.length ? "マーク あり" : "マーク 未設定"}
          </Tag>
        ) : (
          <Tag dot color={rect ? "var(--info)" : "var(--muted)"}>
            {rect ? "bbox あり" : "bbox 未設定"}
          </Tag>
        )}
      </AppBar>

      <main
        className="wrap"
        style={{ padding: "20px 24px 120px", display: "grid", gridTemplateColumns: "74px 1fr", gap: 18, alignItems: "start" }}
      >
        <div className="tool reveal" role="toolbar" aria-label="注釈ツール" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {mode === "bbox" ? (
            <>
              <button title="枠を追加 / リセット" aria-label="枠を追加" onClick={addBox} style={toolBtn(true)}>
                <Icon name="frame" size={18} />
              </button>
              <button title="枠を削除" aria-label="枠を削除" onClick={() => { setRect(null); setDirty(true); }} style={toolBtn(false)} disabled={!rect}>
                <Icon name="backspace" size={18} />
              </button>
              <button title="AIで自動検出" aria-label="AIで自動検出" onClick={runDetect} disabled={detecting || !step} style={toolBtn(false)}>
                {detecting ? <Icon name="spinner" size={18} className="ic-spin" /> : <Icon name="ai-review" size={18} />}
              </button>
            </>
          ) : (
            <>
              <button title="描く" aria-label="蛍光ペンで描く" aria-pressed={hiTool === "draw"} onClick={() => setHiTool("draw")} style={toolBtn(hiTool === "draw")}>
                <Icon name="pen" size={18} />
              </button>
              <button title="消す（マークをタップで削除）" aria-label="マークを消す" aria-pressed={hiTool === "erase"} onClick={() => setHiTool("erase")} style={toolBtn(hiTool === "erase")}>
                <Icon name="eraser" size={18} />
              </button>
              <button title="取り消し（直前のマーク）" aria-label="直前のマークを取り消し" onClick={undoStroke} disabled={!strokes.length} style={toolBtn(false)}>
                <Icon name="undo" size={18} />
              </button>
              <button title="全消去" aria-label="マークを全消去" onClick={clearStrokes} disabled={!strokes.length} style={toolBtn(false)}>
                <Icon name="close" size={18} />
              </button>
              <button title="AIで自動マーク" aria-label="AIで自動マーク" onClick={runAutoMark} disabled={marking || !step} style={toolBtn(false)}>
                {marking ? <Icon name="spinner" size={18} className="ic-spin" /> : <Icon name="ai-review" size={18} />}
              </button>
            </>
          )}
        </div>

        <div>
          <div className="kicker reveal">ANNOTATE — {mode === "marker" ? "重要ポイントをマーク" : "操作対象を明示"}</div>
          <h1 className="display t-lg reveal d1" style={{ margin: "8px 0 14px" }}>
            {mode === "marker" ? (
              <>どこが<span className="hl">大事</span>か、蛍光ペンで。</>
            ) : (
              <>どこを<span className="hl">操作</span>するか、ひと目で。</>
            )}
          </h1>

          {/* Mode switch: 枠(bbox) ↔ マーカー */}
          <div className="row reveal d1" role="tablist" aria-label="注釈モード" style={{ gap: 8, marginBottom: 14 }}>
            <button
              role="tab"
              aria-selected={mode === "bbox"}
              onClick={() => setMode("bbox")}
              className="chip"
              style={modeChip(mode === "bbox")}
            >
              <Icon name="frame" size={16} /> 枠
            </button>
            <button
              role="tab"
              aria-selected={mode === "marker"}
              onClick={() => setMode("marker")}
              className="chip"
              style={modeChip(mode === "marker")}
            >
              <Icon name="pen" size={16} /> マーカー
            </button>
          </div>

          {loading ? (
            <LoadingState label="手順を読み込み中…" />
          ) : error ? (
            <ErrorState message={error} offline={offline} onRetry={reload} />
          ) : !step ? (
            <EmptyState icon={<Icon name="frame" size={30} />} title="編集できる手順がありません">
              この マニュアルには手順がまだありません。
            </EmptyState>
          ) : (
            <>
              <div className="row reveal" style={{ alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                <Tag mono>
                  STEP {String(step.order).padStart(2, "0")} / {steps.length}
                </Tag>
                <span className="jp" style={{ fontWeight: 600 }}>{step.title}</span>
                {step.videoTimecodeMs != null ? <Tag mono>{mmss(step.videoTimecodeMs)}</Tag> : null}
              </div>

              {imgSrc && mode === "marker" ? (
                <div className="canvas reveal d1" style={canvasStyle}>
                  {(["tl", "tr", "bl", "br"] as const).map((c) => (
                    <span key={c} aria-hidden="true" style={trimMark(c)} />
                  ))}
                  <Highlighter
                    src={imgSrc}
                    alt={step.title}
                    value={strokes}
                    onChange={onStrokesChange}
                    color={hiColor}
                    tool={hiTool}
                    style={{ position: "absolute", inset: 0 }}
                  />
                </div>
              ) : imgSrc ? (
                <div className="canvas reveal d1" style={canvasStyle}>
                  {/* Trim marks (トンボ) — corner ticks, design-system flavour */}
                  {(["tl", "tr", "bl", "br"] as const).map((c) => (
                    <span key={c} aria-hidden="true" style={trimMark(c)} />
                  ))}
                  <div
                    ref={stageRef}
                    style={{ position: "absolute", inset: 0, touchAction: "none", userSelect: "none" }}
                  >
                    <img
                      src={imgSrc}
                      alt={step.title}
                      draggable={false}
                      style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                    />
                    {rect ? (
                      <div
                        role="group"
                        aria-label="操作対象の枠"
                        onPointerDown={(e) => beginDrag(e, "move")}
                        style={{
                          position: "absolute",
                          top: `${rect.top}%`,
                          left: `${rect.left}%`,
                          width: `${rect.width}%`,
                          height: `${rect.height}%`,
                          border: "2px solid var(--hi)",
                          borderRadius: 6,
                          boxShadow: "0 0 0 2px rgba(0,0,0,.4)",
                          cursor: "move",
                          touchAction: "none",
                        }}
                      >
                        <span style={overlayLabelStyle}>{overlayLabel}</span>
                        {HANDLES.map((h) => (
                          <span
                            key={h.id}
                            onPointerDown={(e) => beginDrag(e, h.id)}
                            role="slider"
                            aria-label={`${h.id} ハンドル`}
                            tabIndex={-1}
                            style={handleStyle(h.cx, h.cy, h.cursor)}
                          />
                        ))}
                      </div>
                    ) : (
                      <button
                        onClick={addBox}
                        className="statuspill"
                        style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        <Icon name="plus" size={16} /> 枠を追加して囲む
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <EmptyState icon={<Icon name="image-off" size={30} />} title="この手順には画像がありません" className="reveal d1">
                  選択フレームがまだありません。Pickerで代表フレームを確定すると、ここで注釈できます。
                </EmptyState>
              )}

              {mode === "bbox" ? (
                <>
                  {/* Label + AI detect (枠モード固有) */}
                  <div className="reveal d2" style={{ marginTop: 16 }}>
                    <label className="kicker" htmlFor="obj-label">対象ラベル（OBJECT LABEL）</label>
                    <div className="row" style={{ gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                      <input
                        id="obj-label"
                        type="text"
                        value={label}
                        placeholder="例: シール / レバー / ボタン"
                        onChange={(e) => { setLabel(e.target.value); setDirty(true); }}
                        className="jp"
                        style={labelInputStyle}
                      />
                      <Btn variant="ghost" icon={<Icon name="ai-review" size={16} />} onClick={runDetect} disabled={detecting || !imgSrc}>
                        {detecting ? "検出中…" : "AIで自動検出"}
                      </Btn>
                    </div>
                  </div>

                  <Banner variant="ai" className="reveal d2">
                    <span className="jp">
                      <b>AI:</b> 枠をドラッグで移動、四隅・辺のハンドルでリサイズできます。<b>「AIで自動検出」</b>で操作対象を再提案します。
                    </span>
                  </Banner>
                </>
              ) : (
                <>
                  {/* Ink palette (マーカーモード固有) */}
                  <div className="reveal d2" style={{ marginTop: 16 }}>
                    <span className="kicker">マーカーの色</span>
                    <div className="row" role="radiogroup" aria-label="マーカーの色" style={{ gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {HI_PALETTE.map((p) => (
                        <button
                          key={p.color}
                          role="radio"
                          aria-checked={hiColor === p.color}
                          aria-label={p.label}
                          title={p.label}
                          onClick={() => { setHiColor(p.color); setHiTool("draw"); }}
                          style={swatchStyle(p.color, hiColor === p.color)}
                        />
                      ))}
                    </div>
                  </div>

                  <Banner variant="ai" className="reveal d2">
                    <span className="jp">
                      <b>AI:</b> 画像をなぞって<b>蛍光ペン</b>でマーク（マウス/タッチ/ペン対応）。
                    </span>
                  </Banner>
                </>
              )}

              {/* Body key-phrase highlighter — available in BOTH modes (枠 / マーカー).
                  Select text in the body → "マーク", or let the AI propose key phrases. */}
              <div className="reveal d2" style={{ marginTop: 18 }}>
                <div className="row" style={{ alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span className="kicker">本文の重要フレーズ（蛍光ハイライト）</span>
                  <span style={{ flex: 1 }} />
                  <Btn variant="ghost" icon={<Icon name="ai-review" size={16} />} onClick={runAutoMark} disabled={marking || !step}>
                    {marking ? "マーク中…" : "AIで自動マーク"}
                  </Btn>
                </div>
                <div
                  ref={bodyRef}
                  className="jp"
                  onMouseUp={() => setSelText(typeof window !== "undefined" ? window.getSelection()?.toString() ?? "" : "")}
                  style={{
                    marginTop: 8,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid var(--line)",
                    background: "var(--surface)",
                    lineHeight: 1.85,
                    userSelect: "text",
                    WebkitUserSelect: "text",
                  }}
                >
                  <MarkdownText text={step.caption || step.title} terms={terms} />
                </div>
                <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={selText}
                    placeholder="重要フレーズを入力 or 本文を選択"
                    onChange={(e) => setSelText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") markSelection(); }}
                    className="jp"
                    style={labelInputStyle}
                    aria-label="重要フレーズ"
                  />
                  <Btn variant="hi" icon={<Icon name="pen" size={16} />} onClick={markSelection}>
                    マーク
                  </Btn>
                </div>
                {terms.length ? (
                  <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    {terms.map((t) => (
                      <span key={t} className="chip" style={{ gap: 6 }}>
                        <span className="hl">{t}</span>
                        <button
                          aria-label={`「${t}」のマークを削除`}
                          onClick={() => removeTerm(t)}
                          style={{ border: 0, background: "transparent", color: "var(--muted)", cursor: "pointer", lineHeight: 1, display: "inline-flex", alignItems: "center", padding: 12, margin: "-12px -10px -12px -8px" }}
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {notice ? (
                <Banner variant="warn" className="">
                  <span className="jp">{notice}</span>
                </Banner>
              ) : null}
            </>
          )}
        </div>
      </main>

      <div className="actionbar">
        <Btn variant="ghost" lg icon={<Icon name="chevron" size={18} style={{ transform: "rotate(90deg)" }} />} disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
          前の手順
        </Btn>
        <span className="grow">
          <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
            {dirty ? "未保存の変更があります" : mode === "marker" ? "マークは手順画像に保存" : "注釈は手順画像に保存"}
          </span>
        </span>
        {idx < steps.length - 1 ? (
          <Btn variant="ghost" lg onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))}>
            次の手順 <Icon name="chevron" size={18} style={{ transform: "rotate(-90deg)" }} />
          </Btn>
        ) : null}
        <Btn variant="hi" lg icon={<Icon name="target" size={18} />} onClick={save} disabled={saving || loading || !step}>
          {saving ? "保存中…" : "保存"}
        </Btn>
      </div>
    </div>
  );
}

const canvasStyle = {
  position: "relative",
  aspectRatio: "4/3",
  borderRadius: 16,
  overflow: "hidden",
  border: "1px solid var(--line)",
  background: "radial-gradient(120% 90% at 60% 40%,#2a2f3a,#0e1014)",
} as const;

const overlayLabelStyle = {
  position: "absolute",
  top: -9,
  left: -2,
  transform: "translateY(-100%)",
  background: "var(--hi)",
  color: "#10130a",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  fontWeight: 700,
  padding: "1px 6px",
  borderRadius: 4,
  whiteSpace: "nowrap",
  pointerEvents: "none",
} as const;

const labelInputStyle = {
  flex: "1 1 240px",
  minWidth: 0, // 200px min overflowed the ≤340px annotate column
  minHeight: 48,
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid var(--line)",
  background: "var(--surface)",
  color: "var(--ink)",
  fontSize: 15,
} as const;

function toolBtn(active: boolean) {
  return {
    width: 54,
    height: 54,
    borderRadius: 14,
    border: "1px solid var(--line)",
    background: active ? "var(--hi)" : "var(--surface)",
    color: active ? "#10130a" : undefined,
    fontSize: 20,
  } as const;
}

function modeChip(active: boolean): React.CSSProperties {
  return active
    ? { background: "var(--ink)", color: "var(--bg)", borderColor: "var(--ink)" }
    : {};
}

function swatchStyle(color: string, active: boolean): React.CSSProperties {
  return {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: color,
    border: active ? "2px solid var(--ink)" : "1px solid var(--line-2)",
    boxShadow: active ? "0 0 0 4px color-mix(in srgb,var(--hi) 30%,transparent)" : undefined,
    cursor: "pointer",
    padding: 0,
  };
}

function handleStyle(cx: number, cy: number, cursor: string) {
  return {
    position: "absolute",
    left: `${cx * 100}%`,
    top: `${cy * 100}%`,
    width: 18,
    height: 18,
    transform: "translate(-50%,-50%)",
    background: "var(--hi)",
    border: "2px solid #10130a",
    borderRadius: 4,
    cursor,
    touchAction: "none",
  } as const;
}

function trimMark(corner: "tl" | "tr" | "bl" | "br") {
  const base = {
    position: "absolute",
    width: 14,
    height: 14,
    borderColor: "rgba(255,255,255,.45)",
    borderStyle: "solid",
    borderWidth: 0,
    pointerEvents: "none",
    zIndex: 2,
  } as const;
  const pad = 8;
  const side: Record<typeof corner, React.CSSProperties> = {
    tl: { top: pad, left: pad, borderTopWidth: 2, borderLeftWidth: 2 },
    tr: { top: pad, right: pad, borderTopWidth: 2, borderRightWidth: 2 },
    bl: { bottom: pad, left: pad, borderBottomWidth: 2, borderLeftWidth: 2 },
    br: { bottom: pad, right: pad, borderBottomWidth: 2, borderRightWidth: 2 },
  };
  return { ...base, ...side[corner] };
}
